import React, { createRef, useEffect, useState } from 'react'
import { Typography, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { scrollToCategory } from 'src/Libs/shop/utils'
import useShopActiveCategory from 'src/Libs/shop/hooks/use-shop-category-scroll'
import { useHeaderTabs } from '@e3dc-react/shell/Libs/Header'
import { usePageActionsContext } from 'src/Libs/header/context'
import { MainComponentCategoryCard, RetrofitBatteryCategory, ShopMainSkeleton } from 'src/Libs/shop'
import { Components, Product } from 'src/Libs/shop/models/system-configuration-components.model'
import useSelectedUserRoutes from 'src/Hooks/useSelectedUserRoutes'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import QntmCardComponent from './QntmSystemConfiguration/MainComponent/QntmCardComponent'
export interface MainComponentProps {
  title: string
  categories: Components[]
  isLoading: boolean
  showRetrofitBattery?: boolean
  onBack: () => void
  onMoreInfo: (productId: string) => void
  onSelectProduct: (category: Components, product: Product) => void
  isProductSelected: (productId: string, categoryId: string) => boolean
  onAllowContinueChange?: (allow: boolean) => void
  canContinue?: boolean
  qntmProduct?: Product
}

const useStyles = makeStyles()(theme => ({
  title: {
    fontSize: '32px',
    fontWeight: 400,
  },
  systemConfiguration: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
      marginTop: theme.spacing(1),
    },
  },
}))

/*
 * Main Component view rendering categories, tabs, and product selection
 */
const MainComponent: React.FC<MainComponentProps> = ({
  title,
  categories,
  isLoading,
  showRetrofitBattery = false,
  onBack,
  onMoreInfo,
  onSelectProduct,
  isProductSelected,
  onAllowContinueChange,
  canContinue = false,
  qntmProduct,
}) => {
  const { classes } = useStyles()
  const { setTabs, setActiveTab } = useHeaderTabs()
  const { t } = useCustomTranslation()
  const { dispatchPageActions } = usePageActionsContext()
  const [categoryRefs, setCategoryRefs] = useState<React.RefObject<HTMLDivElement>[]>([])
  const { activeCardIndex } = useShopActiveCategory(categoryRefs)

  useEffect(() => {
    dispatchPageActions?.([
      {
        position: 'left',
        type: 'back',
        onClick: onBack,
      },
    ])

    return () => {
      dispatchPageActions?.([])
    }
  }, [dispatchPageActions, onBack])

  useSelectedUserRoutes(t('shop.system-configure.main-component-title'))

  useEffect(() => {
    const newRefs = categories.map(() => createRef<HTMLDivElement>()) as React.RefObject<HTMLDivElement>[]
    setCategoryRefs(newRefs)

    const newTabs = categories.map((category, index) => ({
      label: category.translated.name,
      onClick: () => {
        setActiveTab(index)
        scrollToCategory(newRefs[index])
      },
    }))

    setTabs(newTabs)

    return () => {
      setTabs([])
    }
  }, [categories, setTabs, setActiveTab])

  useEffect(() => {
    if (activeCardIndex !== null) {
      setActiveTab(activeCardIndex)
    }
  }, [activeCardIndex, setActiveTab])

  useEffect(() => {
    onAllowContinueChange?.(canContinue)
  }, [canContinue, onAllowContinueChange])

  if (isLoading) return <ShopMainSkeleton />

  return (
    <Box sx={{ width: '100%', marginRight: 0, marginBottom: '58px', position: 'relative' }}>
      <Typography className={classes.title}>{title}</Typography>

      <Box className={classes.systemConfiguration}>
        {showRetrofitBattery && <RetrofitBatteryCategory />}
        {!isLoading &&
          categories.map((category, index) => (
            <Box key={category.id} ref={categoryRefs[index]}>
              <MainComponentCategoryCard
                categoryData={category}
                onMoreInfo={onMoreInfo}
                onSelectProduct={onSelectProduct}
                isProductSelected={isProductSelected}
              />
            </Box>
          ))}
        {qntmProduct && <QntmCardComponent qntmProduct={qntmProduct} onMoreInfo={onMoreInfo} />}
      </Box>
    </Box>
  )
}

export default MainComponent
